import { type ReactNode, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { LogOut, KeyRound, PenLine, Settings, Mail, Sun, Moon, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslations } from '@/i18n/useTranslations';
import { useTheme } from '@/components/theme-provider';
import { useNavigation } from '@/hooks/use-navigation';
import { useLogout } from '@/hooks/use-logout';
import { useAuthStore } from '@/stores/auth-store';
import { useMessageStore } from '@/stores/message-store';
import { useUiModeStore } from '@/stores/ui-mode-store';
import { useAiChatStore } from '@/stores/ai-chat-store';
import { useShowTryGuidedButton } from '@/hooks/use-guided-ui-mode';
import { useFooterConfig } from '@/hooks/use-footer-config';
import { UserRole } from '@comp-sys/shared';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { LanguageSwitcher } from '@/components/ui/language-switcher';
import { MobileSidebar } from '@/components/layout/mobile-sidebar';
import { BrandLogo } from '@/components/shared/brand-logo';
import { ConfigurableFooter } from '@/components/shared/configurable-footer';
import { ProfileReviewAlert } from '@/components/shared/profile-review-alert';
import { AiChatPanel } from '@/components/ai/ai-chat-panel';
import { AiFloatingButton } from '@/components/ai/ai-floating-button';
import { RoleSwitcherMenuItems } from '@/components/auth/role-switcher';
import { LiquidGlass, LiquidGlassGlobalFilter } from '@/components/liquid/liquid-glass';
import { LiquidMorphIndicator } from '@/components/liquid/liquid-morph-indicator';
import { liquidPageVariants } from '@/lib/motion/presets';
import { ChangePasswordDialog } from '@/components/users/change-password-dialog';
import { ChangeNameDialog } from '@/components/users/change-name-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

/**
 * Liquid 家族 shell（Apple Liquid Glass）——「液璃」。
 *
 * 忠实 iOS 26 / macOS Tahoe 26 的浮动胶囊 chrome：
 * - **浮动胶囊顶栏**（`liquid-glass` + 胶囊形）：logo + 工具 + 用户，sticky 悬浮，玻璃折射滚动内容
 * - **浮动胶囊子导航**（`liquid-glass--clear` segmented）：有二级导航时显示，选中态=胶囊指示器
 * - **浮动胶囊 dock**（`liquid-glass`，iOS 26 浮动 tab bar）：一级主导航，选中态=胶囊指示器，hover 放大
 * - **内容直铺背景**（不包大玻璃基座）：页面卡片/图表浮在背景上，玻璃卡片折射背景与图表色
 *   —— 对应 Apple content-first「让内容透出」（spec §1.3 / §6）
 * - **spring 页面过渡**（easeOutExpo），尊重 prefers-reduced-motion
 *
 * 原子控件形态/材质由 `data-skin="liquid"` CSS 处理；`.liquid-glass*` 是可复用材质原语（所有主题可用）。
 * spec: docs/superpowers/specs/2026-06-27-apple-liquid-glass-faithful-reproduction-design.md §6
 */
export function LiquidShell({ children }: { children: ReactNode }) {
  const location = useLocation();
  const pathname = location.pathname;
  const { navItems } = useNavigation();
  const { config: footerConfig } = useFooterConfig();
  const aiIsOpen = useAiChatStore((s) => s.isOpen);
  const aiIsFloating = useAiChatStore((s) => s.isFloating);
  const user = useAuthStore((state) => state.user);
  const isSystemAdmin = user?.activeRole === 'SYSTEM_ADMIN';
  const t = useTranslations('nav');

  const prefersReducedMotion = useReducedMotion();

  const isActive = (href: string) =>
    pathname === href || (href !== '/dashboard' && pathname.startsWith(href + '/'));

  const activeTop = navItems.find((i) => isActive(i.href) && i.children?.length);
  const subNav = activeTop?.children ?? [];

  return (
    <div className="min-h-screen flex flex-col">
      {/* 全局共享 Liquid Glass filter（Chromium）：浮层 dialog/sheet/dropdown/select/toast
          用 backdrop-filter: url(#liquid-glass-global) 获真折射（CSS 引用，见 globals.css） */}
      <LiquidGlassGlobalFilter />
      <LiquidTopBar />

      <main id="main-content" className="flex-1 px-3 md:px-6 pt-4 pb-32">
        <div className="mx-auto max-w-6xl">
          <ProfileReviewAlert />

          {/* 二级浮动胶囊子导航（iOS 26 segmented capsule） */}
          {subNav.length > 0 && (
            <LiquidGlass
              as="nav"
              variant="clear"
              radius={999}
              className="sticky top-[68px] z-20 mb-5 flex w-full flex-wrap gap-1.5 p-1"
            >
              {subNav.map((child) => {
                const childActive = isActive(child.href);
                return (
                  <Link
                    key={child.href}
                    to={child.href}
                    className="relative flex items-center rounded-full px-4 py-1.5 text-[13px] font-medium transition-colors duration-200"
                  >
                    {childActive && <LiquidMorphIndicator layoutId="liquid-subnav-active" />}
                    <span
                      className={cn(
                        'relative z-[1] transition-colors',
                        childActive
                          ? 'text-foreground'
                          : 'text-muted-foreground hover:text-foreground',
                      )}
                    >
                      {t(child.labelKey)}
                    </span>
                  </Link>
                );
              })}
            </LiquidGlass>
          )}

          {/* 内容直铺背景：页面卡片/图表浮在背景上，玻璃折射其色（content-first） */}
          <AnimatePresence mode="wait">
            <motion.div
              key={pathname}
              variants={liquidPageVariants}
              initial={prefersReducedMotion ? false : 'initial'}
              animate="animate"
              exit={prefersReducedMotion ? undefined : 'exit'}
            >
              {children}
            </motion.div>
          </AnimatePresence>

          <div className="mt-8">
            <ConfigurableFooter variant="dashboard" config={footerConfig} />
          </div>
        </div>
      </main>

      {/* 浮动胶囊 dock —— iOS 26 浮动 tab bar（一级主导航） */}
      <LiquidDock navItems={navItems} isActive={isActive} />

      {isSystemAdmin && aiIsOpen && !aiIsFloating && <AiChatPanel mode="fixed" />}
      {isSystemAdmin && aiIsFloating && <AiChatPanel mode="floating" />}
      {isSystemAdmin && !aiIsOpen && <AiFloatingButton />}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* 浮动胶囊顶栏                                                        */
/* ------------------------------------------------------------------ */

function LiquidTopBar() {
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const unreadCount = useMessageStore((s) => s.unreadCount);
  const tCommon = useTranslations('common');
  // 引导式回切:资格角色在经典模式下可见,点此回到引导式首页。
  const setUiMode = useUiModeStore((s) => s.setUiMode);
  const guidedEligible = useShowTryGuidedButton();
  const tGuided = useTranslations('guided');
  const [menuOpen, setMenuOpen] = useState(false);

  // iOS 导航栏 scroll-edge 行为：内容滚入栏下时玻璃增强（更深漫射阴影 + 底部接触）。
  // 尊重 prefers-reduced-motion（transition 仍走，仅 ~0.3s，非位移）。
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header data-scrolled={scrolled} className="sticky top-0 z-30 px-3 md:px-6 pt-3">
      <LiquidGlass radius={999} displacementScale={30} className="mx-auto max-w-6xl flex w-full items-center gap-3 px-4 py-2">
        <div className="md:hidden">
          <MobileSidebar />
        </div>
        <BrandLogo variant="auto" size="sm" linkTo="/" />
        <span className="flex-1" />

        {guidedEligible && (
          <Button
            variant="ghost"
            size="sm"
            className="rounded-full"
            onClick={() => setUiMode('guided')}
            aria-label={tGuided('switchGuided')}
            title={tGuided('switchGuided')}
          >
            <Sparkles className="h-4 w-4" />
            <span className="ml-1 hidden lg:inline">{tGuided('switchGuided')}</span>
          </Button>
        )}

        <LanguageSwitcher />
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9 rounded-full"
          onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
          aria-label={tCommon('toggleTheme')}
        >
          <Sun className="h-4 w-4 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
          <Moon className="absolute h-4 w-4 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="relative h-9 w-9 rounded-full"
          onClick={() => navigate('/dashboard/messages')}
          aria-label={tCommon('openMessages')}
        >
          <Mail className="h-4 w-4" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-primary text-primary-foreground text-[10px] font-bold px-1">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </Button>

        <LiquidUserMenu menuOpen={menuOpen} setMenuOpen={setMenuOpen} />
      </LiquidGlass>
    </header>
  );
}

function LiquidUserMenu({ menuOpen, setMenuOpen }: { menuOpen: boolean; setMenuOpen: (v: boolean) => void }) {
  const user = useAuthStore((state) => state.user);
  const navigate = useNavigate();
  const { logout, isPending } = useLogout();
  const tAuth = useTranslations('auth');
  const tUsers = useTranslations('users');
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [changeNameOpen, setChangeNameOpen] = useState(false);
  const isPrivileged =
    user?.activeRole === UserRole.SYSTEM_ADMIN || user?.activeRole === UserRole.SUPER_ADMIN;

  return (
    <>
      <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
        <DropdownMenuTrigger className="rounded-full p-0.5 hover:bg-overlay/60 transition-colors cursor-pointer">
          <Avatar className="h-8 w-8 rounded-full border border-border">
            <AvatarImage src="/avatar.png" />
            <AvatarFallback className="rounded-full text-[11px]">
              {user?.displayName?.[0]?.toUpperCase() || 'U'}
            </AvatarFallback>
          </Avatar>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          <div className="px-2 py-1.5">
            <p className="text-sm font-medium truncate">{user?.displayName || user?.email}</p>
          </div>
          <DropdownMenuSeparator />
          <RoleSwitcherMenuItems onSwitchSuccess={() => setMenuOpen(false)} />
          <DropdownMenuItem onClick={() => setChangeNameOpen(true)}>
            <PenLine className="h-4 w-4" />
            {tUsers('changeName')}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setChangePasswordOpen(true)}>
            <KeyRound className="h-4 w-4" />
            {tUsers('changePassword')}
          </DropdownMenuItem>
          {isPrivileged && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/dashboard/settings')}>
                <Settings className="h-4 w-4" />
                {tAuth('logout.settings')}
              </DropdownMenuItem>
            </>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={logout} disabled={isPending} className="text-error-text">
            <LogOut className="h-4 w-4" />
            {tAuth('logout.title')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <ChangePasswordDialog open={changePasswordOpen} onOpenChange={setChangePasswordOpen} />
      <ChangeNameDialog open={changeNameOpen} onOpenChange={setChangeNameOpen} />
    </>
  );
}

/* ------------------------------------------------------------------ */
/* 浮动胶囊 dock（iOS 26 浮动 tab bar —— 一级主导航）                     */
/* ------------------------------------------------------------------ */

function LiquidDock({
  navItems,
  isActive,
}: {
  navItems: ReturnType<typeof useNavigation>['navItems'];
  isActive: (href: string) => boolean;
}) {
  const t = useTranslations('nav');
  const prefersReducedMotion = useReducedMotion();

  // 仅渲染叶子导航项（一级主导航）；图标 dock，hover 放大 + tooltip，选中=胶囊指示器
  const items = navItems;

  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 max-w-[calc(100vw-1.5rem)]">
      <LiquidGlass radius={999} displacementScale={30} className="flex items-end gap-1 px-2 py-2 max-w-[calc(100vw-1.5rem)]">
        {items.map((item) => {
          const active = isActive(item.href);
          const Icon = item.icon;
          return (
            <Link
              key={item.href}
              to={item.href}
              title={t(item.labelKey)}
              className={cn(
                'group relative flex flex-col items-center justify-center rounded-full w-11 h-11',
                prefersReducedMotion
                  ? 'active:scale-[0.94]'
                  : 'transition-transform duration-200 ease-[cubic-bezier(0.16,1,0.3,1)] hover:-translate-y-1 hover:scale-105 active:scale-[0.92]',
              )}
            >
              {/* iOS 26 浮动 tab bar：选中=液态 morph 玻璃胶囊（spring 流动）；非选中无填充 */}
              {active && <LiquidMorphIndicator layoutId="liquid-dock-active" />}
              <Icon
                className={cn(
                  'relative z-[1] h-[18px] w-[18px] transition-colors',
                  active
                    ? 'text-foreground'
                    : 'text-muted-foreground group-hover:text-foreground',
                )}
              />
              {/* 悬停标签 tooltip（iOS 暗玻璃胶囊） */}
              <span className="liquid-tooltip pointer-events-none absolute -top-9 whitespace-nowrap px-2 py-1 text-[11px] font-medium opacity-0 scale-90 transition-all duration-150 group-hover:opacity-100 group-hover:scale-100">
                {t(item.labelKey)}
              </span>
            </Link>
          );
        })}
      </LiquidGlass>
    </div>
  );
}
